import { useState, useEffect, useSyncExternalStore, createContext, useContext, ReactNode, useCallback, useRef } from 'react';
import { driverSupabase as supabase } from '@/integrations/supabase/driver-client';
import { activeTripStore } from '@/lib/activeTripStore';
import { toast } from 'sonner';
import {
  DRIVER_SESSION_KEY,
  SESSION_INVALIDATED_EVENT,
  getDriverSessionId,
  setDriverSessionId,
} from '@/lib/driverSession';
import {
  getCachedFcmToken,
  getLastRegisteredFcm,
  mintFreshFcmToken,
  postRegisterFcmToken,
  setLastRegisteredFcm,
} from '@/hooks/useFcmRegistration';
import { clearPrimedFcmToken, waitForFcmToken } from '@/lib/fcmBootstrap';
import { useDriverHeartbeat } from '@/hooks/useDriverHeartbeat';
import { GpsBuffer } from '@/plugins/gps-buffer';
import { approachBookingStore } from '@/lib/approachBookingStore';

interface DriverProfile {
  id: string;
  full_name: string;
  phone: string;
  driver_id: string | null;
  photo_url: string | null;
  rating: number | null;
  is_available: boolean | null;
  status: string | null;
  vehicle_id: string | null;
  vehicle_registration: string | null;
  vehicle_row_id?: string | null;
  vendor_id?: string | null;
  active_session_id?: string | null;
  suspension_reason?: string | null;

}

interface DriverAuthContextType {
  driver: DriverProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  sessionId: string | null;
  sendOtp: (phone: string) => Promise<{ success: boolean; error?: string; driverName?: string }>;
  verifyOtp: (phone: string, otp: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  refreshDriver: (opts?: { force?: boolean }) => Promise<void>;
  setDriverAvailabilityLocal: (isAvailable: boolean) => void;
  setDriverVehicleLocal: (v: { vehicle_row_id: string; vehicle_id: string | null; vehicle_registration: string | null }) => void;
}

const DRIVER_STORAGE_KEY = 'driver_profile_cache';
const DRIVER_AUTH_TOKEN_KEY = 'driver-auth-token';
const FORCE_SIGNED_OUT_KEY = 'driver_force_signed_out';
// TTL for the force-signed-out marker. Long enough to survive the immediate
// post-sign-out reload, short enough that an OOM-induced cold start 30s+
// later doesn't keep bouncing the user back to /driver/login.
const FORCE_SIGNED_OUT_TTL_MS = 10_000;
// Suppression window for forced sign-outs while the user is inside a native
// camera / file-picker intent. Android can OOM-kill the WebView while the
// system Camera Activity is foreground; the relaunch race must not log the
// driver out. Kept in sync with markUploadIntent() in src/lib/nativeCamera.ts.
const UPLOAD_INTENT_SUPPRESS_MS = 15_000;

function isInUploadIntentWindow(): boolean {
  try {
    const raw = localStorage.getItem('driver_intent_window');
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < UPLOAD_INTENT_SUPPRESS_MS;
  } catch {
    return false;
  }
}

function setForceSignedOutMarker() {
  try { localStorage.setItem(FORCE_SIGNED_OUT_KEY, String(Date.now())); } catch { /* ignore */ }
}

function readForceSignedOutMarker(): boolean {
  try {
    const raw = localStorage.getItem(FORCE_SIGNED_OUT_KEY);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (!Number.isFinite(at)) {
      try { localStorage.removeItem(FORCE_SIGNED_OUT_KEY); } catch {}
      return false;
    }
    if (Date.now() - at > FORCE_SIGNED_OUT_TTL_MS) {
      try { localStorage.removeItem(FORCE_SIGNED_OUT_KEY); } catch {}
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function clearForceSignedOutMarker() {
  try { localStorage.removeItem(FORCE_SIGNED_OUT_KEY); } catch { /* ignore */ }
}

/**
 * Wipe the device's Firebase Installation so the next login mints a brand-new
 * FCM token. Best-effort, Android-native only, never throws. Called on every
 * sign-out path (manual signOut + forceSignOutForOtherDevice) so a device can
 * never reuse its previous FCM token across login cycles.
 */
async function wipeDeviceFcmIdentity(): Promise<void> {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return;
    const { FCM } = await import('@capacitor-community/fcm');
    try { await FCM.deleteInstance(); } catch { /* non-fatal */ }
  } catch { /* not native or plugin missing */ }
}

function saveDriverToStorage(driver: DriverProfile | null) {
  if (driver) {
    localStorage.setItem(DRIVER_STORAGE_KEY, JSON.stringify(driver));
  } else {
    localStorage.removeItem(DRIVER_STORAGE_KEY);
  }
}

function loadDriverFromStorage(): DriverProfile | null {
  try {
    const raw = localStorage.getItem(DRIVER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const DriverAuthContext = createContext<DriverAuthContextType | undefined>(undefined);

export function DriverAuthProvider({ children }: { children: ReactNode }) {
  const [driver, setDriverState] = useState<DriverProfile | null>(loadDriverFromStorage);
  const [loading, setLoading] = useState(true);
  // True while the driver has an accepted booking that hasn't completed
  // (assigned / driver_reached / in_progress). Drivers.is_available flips to
  // false on acceptance, and activeTripStore only populates at in_progress —
  // without this, the location heartbeat (and thus the customer-map marker)
  // would go silent for the whole driver-approaching phase.
  const hasApproachBooking = useSyncExternalStore(
    approachBookingStore.subscribe,
    approachBookingStore.get,
    approachBookingStore.get,
  );
  const [sessionId, setSessionIdState] = useState<string | null>(getDriverSessionId());

  // Debounce refresh: prevent duplicate calls within 5s
  const lastRefreshRef = useRef<number>(0);
  const sessionChannelRef = useRef<any>(null);
  const forcedOutRef = useRef<boolean>(false);
  // True only while an intentional sign-out is in flight (user tap Log Out,
  // or single-device force-sign-out). Used to distinguish a real logout
  // from a spurious SIGNED_OUT emitted by the Supabase auth client after
  // a transient token-refresh failure on flaky mobile networks.
  const intentionalSignOutRef = useRef<boolean>(false);
  // Bounded session-recovery loop state. See recoverSession() below.
  const recoveringRef = useRef<boolean>(false);
  const recoverAttemptsRef = useRef<number>(0);
  const recoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the wall-clock time of the most recent optimistic local write to
  // `driver.is_available` (via setDriverAvailabilityLocal). Used to ignore
  // any background server refresh whose response disagrees with the fresh
  // local value within a short window — eliminates the race where a
  // TOKEN_REFRESHED or cold-start refresh fired BEFORE the server commit
  // would otherwise clobber the just-toggled state.
  const lastOptimisticWriteRef = useRef<number>(0);

  const setDriver = useCallback((d: DriverProfile | null) => {
    setDriverState(d);
    saveDriverToStorage(d);
  }, []);

  // ── Bounded session-recovery loop ─────────────────────────────────────
  // When the Supabase auth client loses its session because of a transient
  // network / token-refresh failure, we DO NOT log the driver out. Instead
  // we keep the cached driver profile mounted and retry recovery in the
  // background. Only an explicit server rejection of the refresh token
  // (invalid_grant / refresh_token_not_found) after the retry budget is
  // exhausted is treated as unrecoverable.
  const cancelRecovery = useCallback(() => {
    if (recoverTimerRef.current) {
      clearTimeout(recoverTimerRef.current);
      recoverTimerRef.current = null;
    }
    recoveringRef.current = false;
  }, []);

  const recoverSession = useCallback((reason: string) => {
    if (forcedOutRef.current || intentionalSignOutRef.current) return;
    if (!loadDriverFromStorage()) return; // no cached driver — nothing to recover
    if (recoveringRef.current) return;
    recoveringRef.current = true;
    recoverAttemptsRef.current = 0;

    // Exponential backoff (ms): 2s, 4s, 8s, 16s, 30s cap.
    const backoff = [2000, 4000, 8000, 16000, 30000];
    let unrecoverable = false;

    const tick = async () => {
      if (forcedOutRef.current || intentionalSignOutRef.current) {
        cancelRecovery();
        return;
      }
      recoverAttemptsRef.current += 1;
      const attempt = recoverAttemptsRef.current;
      try {
        // 1) Try the client's cached session first — cheapest path.
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.user_metadata?.role === 'driver') {
          console.log(`[auth] recoverSession (${reason}) succeeded via getSession on attempt ${attempt}`);
          cancelRecovery();
          return;
        }
        // 2) Ask the client to refresh using its own persisted refresh token.
        const { data, error } = await supabase.auth.refreshSession();
        if (data?.session?.user?.user_metadata?.role === 'driver') {
          console.log(`[auth] recoverSession (${reason}) succeeded via refreshSession on attempt ${attempt}`);
          cancelRecovery();
          return;
        }
        if (error) {
          const status = (error as any)?.status;
          const msg = String((error as any)?.message || '').toLowerCase();
          // Definitive server rejection of the refresh token. Network
          // errors surface with status=0 / fetch failures — those are NOT
          // unrecoverable.
          const isDefinitive =
            status === 400 ||
            status === 401 ||
            /invalid[_ ]grant|refresh[_ ]token[_ ]not[_ ]found|refresh[_ ]token[_ ]already[_ ]used|invalid[_ ]refresh[_ ]token/.test(msg);
          if (isDefinitive) {
            unrecoverable = true;
          } else {
            console.warn(`[auth] recoverSession (${reason}) attempt ${attempt} network error:`, msg || error);
          }
        } else {
          console.warn(`[auth] recoverSession (${reason}) attempt ${attempt} no session yet`);
        }
      } catch (err) {
        console.warn(`[auth] recoverSession (${reason}) attempt ${attempt} threw:`, err);
      }

      if (unrecoverable && attempt >= backoff.length) {
        console.warn(`[auth] recoverSession (${reason}) unrecoverable after ${attempt} attempts — signing out`);
        cancelRecovery();
        toast.error('Your session has expired — please sign in again.');
        forceSignOutForOtherDevice();
        return;
      }
      if (attempt >= backoff.length) {
        // Retry budget exhausted but no definitive rejection — wait for
        // the next external trigger (online / resume) to restart the loop.
        console.warn(`[auth] recoverSession (${reason}) exhausted ${attempt} attempts, waiting for next trigger`);
        cancelRecovery();
        return;
      }
      const delay = backoff[attempt] ?? 30000;
      recoverTimerRef.current = setTimeout(tick, delay);
    };

    // First attempt runs after a small delay to let a just-restored
    // network settle.
    recoverTimerRef.current = setTimeout(tick, backoff[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelRecovery]);

  // ── Single-device session enforcement ──────────────────────────────────
  // Compares the locally stored session id against the server's
  // `drivers.active_session_id`. If they differ, the driver was logged in
  // on another device — clear local auth and bounce them to /driver/login.
  const forceSignOutForOtherDevice = useCallback(async () => {
    if (forcedOutRef.current) return;
    // Suppress while the user is inside a native camera/picker intent:
    // Android can OOM-kill the WebView while the system Camera Activity is
    // foreground; the relaunch validate_session race must not bounce the
    // driver back to /driver/login.
    if (isInUploadIntentWindow()) {
      console.warn('[auth] force-signout suppressed (upload intent window)');
      return;
    }
    forcedOutRef.current = true;
    intentionalSignOutRef.current = true;
    cancelRecovery();
    // Mark as forced-out FIRST so any concurrent bootstrap / remount of
    // DriverHome (which can read cached driver from localStorage) sees the
    // marker and bails out instead of restoring the old session. Stored in
    // localStorage with a 10s TTL — sessionStorage is wiped on WebView
    // process kill, which made the marker useless after an OOM crash.
    setForceSignedOutMarker();
    if (sessionChannelRef.current) {
      try { supabase.removeChannel(sessionChannelRef.current); } catch { /* ignore */ }
      sessionChannelRef.current = null;
    }
    // Clear ALL local auth/profile storage SYNCHRONOUSLY before navigating.
    // Doing the async signOut() first lets `/driver/home` remount during
    // the await and read stale cached driver state, which is what made the
    // 3rd device-switch only "refresh" instead of redirecting to login.
    try { localStorage.removeItem(DRIVER_AUTH_TOKEN_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(DRIVER_STORAGE_KEY); } catch { /* ignore */ }
    setLastRegisteredFcm(null);
    setDriverSessionId(null);
    setSessionIdState(null);
    setDriver(null);
    try { activeTripStore.clear?.(); } catch { /* ignore */ }
    try { approachBookingStore.clear(); } catch { /* ignore */ }
    clearPrimedFcmToken();
    // Async signOut runs in the background — storage is already gone so
    // even if the auth client races, it cannot restore a session.
    try { supabase.auth.signOut().catch(() => {}); } catch { /* ignore */ }
    // Wipe the Firebase Installation so this device cannot reuse its
    // previous FCM token on its next login. Fire-and-forget.
    wipeDeviceFcmIdentity();
    toast.error('You were signed out because your account was used on another device.');
    if (typeof window !== 'undefined' && window.location.pathname !== '/driver/login') {
      // assign() is treated as a real navigation by Android WebView, while
      // replace() can collapse into a same-document refresh that remounts
      // the home route from cache. Combined with the forced-out marker
      // above, this guarantees we land on /driver/login.
      window.location.assign('/driver/login');
    }
    // forcedOutRef stays true for the remainder of this session — the next
    // verifyOtp() resets it after a fresh login.
  }, [setDriver]);

  const refreshDriverViaEdge = useCallback(async (driverId: string): Promise<DriverProfile | null> => {
    if (forcedOutRef.current) return null;
    try {
      const res = await supabase.functions.invoke('dispatch-trip', {
        body: { action: 'get_driver_profile', driver_id: driverId },
      });
      if (res.data?.success && res.data.driver) {
        const d = res.data.driver as DriverProfile;
        return d;
      }
    } catch (err) {
      console.error('Failed to refresh driver via edge:', err);
    }
    return null;
  }, []);

  // Bounded retry wrapper for cold-start / resume refreshes. Toggle-driven
  // refreshes still use the single-shot refreshDriverViaEdge directly.
  const refreshDriverViaEdgeWithRetry = useCallback(async (driverId: string, attempts = 3): Promise<DriverProfile | null> => {
    for (let i = 0; i < attempts; i++) {
      if (forcedOutRef.current) return null;
      const d = await refreshDriverViaEdge(driverId);
      if (d) return d;
      // Linear back-off: 800ms, 1600ms, 2400ms
      await new Promise(r => setTimeout(r, 800 * (i + 1)));
    }
    return null;
  }, [refreshDriverViaEdge]);

  // Commit a freshly-fetched profile, but ignore the `is_available` value if
  // the user toggled it locally within the last 5s and the server still says
  // the old value (read-your-own-write race against the dispatch-trip commit).
  // Other fields are always taken from the server.
  const commitFreshDriver = useCallback((fresh: DriverProfile) => {
    setDriverState(prev => {
      const recentOptimistic = Date.now() - lastOptimisticWriteRef.current < 5000;
      let next = fresh;
      if (recentOptimistic && prev && prev.is_available !== fresh.is_available) {
        next = { ...fresh, is_available: prev.is_available };
      }
      saveDriverToStorage(next);
      return next;
    });
  }, []);

  useEffect(() => {
    checkExistingSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        // Only honor SIGNED_OUT when we know it is intentional (manual
        // signOut / force-signout for other device). A spurious SIGNED_OUT
        // from the auth client after a failed token refresh must NOT log
        // the driver out — we keep the cached profile and try to recover
        // the session in the background.
        if (intentionalSignOutRef.current || readForceSignedOutMarker()) {
          setDriver(null);
        } else {
          console.warn('[auth] ignoring spurious SIGNED_OUT — treating as transient');
          recoverSession('spurious-signed-out');
        }
        return;
      }
      // Defer all other handling out of the auth callback to avoid the
      // documented Supabase deadlock and to prevent racing with verifyOtp,
      // which already seeded `driver` synchronously and stored the
      // session_id. A redundant refresh here was the reload loop trigger.
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        const meta = session?.user?.user_metadata;
        if (meta?.role === 'driver' && !forcedOutRef.current) {
          setTimeout(() => {
            refreshDriverViaEdge(meta.driver_id).then(d => { if (d) commitFreshDriver(d); });
          }, 0);
        }
      }
    });

    // Listen for the global `session_invalidated` signal raised by any
    // dispatch-trip call site (see src/lib/driverSession.ts).
    const onInvalidated = () => { forceSignOutForOtherDevice(); };
    window.addEventListener(SESSION_INVALIDATED_EVENT, onInvalidated as EventListener);

    // External triggers that (re)start the recovery loop. Each one resets
    // the attempt counter so a fresh network / resume gets a full budget.
    const onOnlineRecover = () => { cancelRecovery(); recoverSession('online'); };
    const onVisibleRecover = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        cancelRecovery();
        recoverSession('visible');
      }
    };
    window.addEventListener('online', onOnlineRecover);
    document.addEventListener('visibilitychange', onVisibleRecover);
    let removeAppStateListener: (() => void) | null = null;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import('@capacitor/app');
        const sub = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) { cancelRecovery(); recoverSession('app-active'); }
        });
        removeAppStateListener = () => { try { sub.remove(); } catch {} };
      } catch { /* ignore */ }
    })();

    return () => {
      subscription.unsubscribe();
      window.removeEventListener(SESSION_INVALIDATED_EVENT, onInvalidated as EventListener);
      window.removeEventListener('online', onOnlineRecover);
      document.removeEventListener('visibilitychange', onVisibleRecover);
      if (removeAppStateListener) removeAppStateListener();
      cancelRecovery();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe to changes on this driver's row so another device taking over
  // the session is detected within seconds. This is the ONLY mechanism that
  // logs out the old device — no polling, no validate_session calls.
  useEffect(() => {
    if (!driver?.id) return;
    const driverId = driver.id;
    let cancelled = false;
    let channel: any = null;
    let broadcastChannel: any = null;
    let resubscribeTimer: ReturnType<typeof setTimeout> | null = null;
    let lastCheckAt = 0;

    const handleUpdate = (payload: any) => {
      const next = payload?.new?.active_session_id;
      const stored = localStorage.getItem(DRIVER_SESSION_KEY);
      // If another device replaced this session, the server row's
      // active_session_id will differ from the one we cached at login.
      if (next && stored && next !== stored) {
        forceSignOutForOtherDevice();
      }
    };

    // Server-authoritative session check. Goes through the dispatch-trip
    // edge function (service role) so it bypasses RLS entirely and works
    // even if the external DB's RLS policy on `drivers` isn't deployed.
    // Debounced to once per 2s across all triggers (resume, focus, online,
    // realtime reconnect) to avoid hammering the edge function.
    const checkServerSession = async () => {
      if (cancelled || forcedOutRef.current) return;
      // Don't fire while backgrounded — the resume handler will pick it up.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      // Skip while a native camera/picker intent is in flight.
      if (isInUploadIntentWindow()) return;
      const stored = localStorage.getItem(DRIVER_SESSION_KEY);
      if (!stored) return;
      const now = Date.now();
      // Debounce the 4-way listener storm (visibilitychange / focus /
      // online / appStateChange) that can fire within ~200ms after a
      // camera intent returns. 30s is plenty — realtime catches anything
      // legitimate sooner.
      if (now - lastCheckAt < 30_000) return;
      lastCheckAt = now;
      try {
        const res = await supabase.functions.invoke('dispatch-trip', {
          body: { action: 'validate_session', driver_id: driverId, session_id: stored },
        });
        // Only force sign-out on an EXPLICIT valid:false from the server.
        // Network errors, undefined responses, or missing `success` are
        // treated as "keep current session" — realtime / next event will
        // catch up. Prevents OOM-induced cold starts from logging users out.
        if (res?.data?.success === true && res?.data?.valid === false) {
          forceSignOutForOtherDevice();
        }
      } catch (err) {
        // Network errors are non-fatal — Realtime/next action will catch up.
        console.warn('[auth] validate_session failed', err);
      }
    };

    const subscribe = () => {
      if (cancelled) return;
      // Tear down any prior channel before re-subscribing to avoid duplicates.
      if (sessionChannelRef.current) {
        try { supabase.removeChannel(sessionChannelRef.current); } catch { /* ignore */ }
        sessionChannelRef.current = null;
      }
      channel = supabase
        .channel(`driver-session-${driverId}`)
        .on('postgres_changes' as any, {
          event: 'UPDATE',
          schema: 'public',
          table: 'drivers',
          filter: `id=eq.${driverId}`,
        }, handleUpdate)
        .subscribe((status: string) => {
          if (status === 'SUBSCRIBED') {
            console.log('[auth] driver-session realtime: SUBSCRIBED');
            // Catch any UPDATE/broadcast that fired while we were
            // disconnected (mobile background, network blip, etc.).
            checkServerSession();
          } else if (
            status === 'CHANNEL_ERROR' ||
            status === 'TIMED_OUT' ||
            status === 'CLOSED'
          ) {
            console.warn(`[auth] driver-session realtime ${status} — resubscribing`);
            if (resubscribeTimer) clearTimeout(resubscribeTimer);
            resubscribeTimer = setTimeout(subscribe, 1500);
          }
        });
      sessionChannelRef.current = channel;
    };

    subscribe();

    // Secondary signal: a server-sent broadcast on `driver-session:<id>`.
    // Works even if the postgres_changes RLS policy on `drivers` isn't set
    // up on the external DB. `driver-login` sends `session_replaced` after
    // minting a new session id.
    const subscribeBroadcast = () => {
      if (cancelled) return;
      try {
        broadcastChannel = supabase
          .channel(`driver-session:${driverId}`)
          .on('broadcast' as any, { event: 'session_replaced' }, (msg: any) => {
            const next = msg?.payload?.new_session_id;
            const stored = localStorage.getItem(DRIVER_SESSION_KEY);
            if (next && stored && next !== stored) {
              forceSignOutForOtherDevice();
            }
          })
          .subscribe();
      } catch (err) {
        console.warn('[auth] broadcast subscribe failed', err);
      }
    };
    subscribeBroadcast();

    // Resume / focus / online session checks. Catches the case where the
    // old device was backgrounded or offline when the new device logged in
    // and therefore missed both Realtime signals.
    const removers: Array<() => void> = [];
    const onVisible = () => { if (document.visibilityState === 'visible') checkServerSession(); };
    const onFocus = () => checkServerSession();
    const onOnline = () => checkServerSession();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    removers.push(() => document.removeEventListener('visibilitychange', onVisible));
    removers.push(() => window.removeEventListener('focus', onFocus));
    removers.push(() => window.removeEventListener('online', onOnline));
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import('@capacitor/app');
        const sub = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            checkServerSession();
            // Force a non-debounced profile refresh so `is_available`
            // (and other status fields) re-sync from the server when the
            // app returns to foreground after being backgrounded or killed.
            refreshDriverViaEdgeWithRetry(driverId).then(fresh => {
              if (fresh) commitFreshDriver(fresh);
            }).catch(() => {});
          }
        });
        removers.push(() => { try { sub.remove(); } catch {} });
      } catch (err) {
        console.warn('[auth] resume listener setup failed', err);
      }
    })();

    return () => {
      cancelled = true;
      if (resubscribeTimer) clearTimeout(resubscribeTimer);
      try { if (sessionChannelRef.current) supabase.removeChannel(sessionChannelRef.current); } catch { /* ignore */ }
      sessionChannelRef.current = null;
      try { if (broadcastChannel) supabase.removeChannel(broadcastChannel); } catch { /* ignore */ }
      broadcastChannel = null;
      for (const r of removers) { try { r(); } catch {} }
    };
  }, [driver?.id, forceSignOutForOtherDevice]);

  const checkExistingSession = async () => {
    try {
      // If the previous tab/page was force-signed-out within the TTL window,
      // do not restore any cached driver — wait for an explicit fresh login.
      // After the TTL expires (10s), an OOM-induced cold start is treated
      // as a normal restore: the user stays signed in.
      if (readForceSignedOutMarker()) {
        try { localStorage.removeItem(DRIVER_AUTH_TOKEN_KEY); } catch {}
        try { localStorage.removeItem(DRIVER_STORAGE_KEY); } catch {}
        setDriverSessionId(null);
        setSessionIdState(null);
        setDriver(null);
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.user_metadata?.role === 'driver') {
        const driverId = session.user.user_metadata.driver_id;
        const cached = loadDriverFromStorage();
        const storedSessionId = getDriverSessionId();

        // Server-authoritative single-device check on every bootstrap.
        // We do this NON-blockingly: if a valid cache exists we render it
        // immediately and only force sign-out on an EXPLICIT `valid:false`.
        // Network errors / missing responses are treated as "keep session"
        // — this is the fix for the "crashes back to login on cold start"
        // bug where an OOM kill + flaky network at relaunch would log the
        // user out.
        const validatePromise = (async () => {
          if (!storedSessionId) return true;
          if (isInUploadIntentWindow()) return true;
          try {
            const res = await supabase.functions.invoke('dispatch-trip', {
              body: { action: 'validate_session', driver_id: driverId, session_id: storedSessionId },
            });
            if (res.data?.success && res.data.valid === false) {
              forceSignOutForOtherDevice();
              return false;
            }
          } catch { /* network error — let realtime catch up */ }
          return true;
        })();

        // KEY OPTIMIZATION: If we have a valid cache, show it immediately and set loading=false
        if (cached && cached.id === driverId) {
          setDriverState(cached);
          setLoading(false); // Unblock UI immediately

          // Background refresh (non-blocking)
          validatePromise.then(ok => {
            if (!ok) return;
            refreshDriverViaEdgeWithRetry(driverId).then(fresh => {
              if (fresh) commitFreshDriver(fresh);
            });
          });
          // Reconcile a possibly-stale activeTripStore entry against the DB
          // so the Sign Out button in Profile is re-enabled without waiting
          // for the user to open DriverHome. Non-blocking.
          const stored = activeTripStore.get();
          if (stored?.bookingId) {
            supabase
              .from('bookings' as any)
              .select('status')
              .eq('id', stored.bookingId)
              .maybeSingle()
              .then(({ data, error }) => {
                if (error) return;
                const status = (data as any)?.status;
                if (status && status !== 'in_progress') activeTripStore.clear();
              });
          }
          return;
        }

        // No valid cache — must wait for fresh data
        const ok = await validatePromise;
        if (!ok) { setDriver(null); return; }
        const fresh = await refreshDriverViaEdgeWithRetry(driverId);
        if (fresh) {
          setDriver(fresh);
        } else {
          setDriver(null);
        }
      } else {
        // No auth session AND not a driver. If a cached driver profile
        // exists (and we are not intentionally signed out), the auth
        // client likely dropped its session after a failed background
        // refresh — keep the cached profile mounted and try to recover
        // in the background instead of bouncing the user to /login.
        const cachedFallback = loadDriverFromStorage();
        if (cachedFallback && !readForceSignedOutMarker()) {
          console.warn('[auth] cold-start: no auth session but cached driver present — attempting recovery');
          setDriverState(cachedFallback);
          recoverSession('cold-start-miss');
        } else {
          setDriver(null);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshDriver = useCallback(async (opts?: { force?: boolean }) => {
    if (!driver?.id) return;
    // Debounce: skip if called within 5s of last refresh (unless `force`)
    const now = Date.now();
    if (!opts?.force && now - lastRefreshRef.current < 5000) return;
    lastRefreshRef.current = now;

    const fresh = await refreshDriverViaEdge(driver.id);
    if (fresh) setDriver(fresh);
  }, [driver?.id, refreshDriverViaEdge, setDriver]);

  // Optimistic local cache update for `is_available`. Used right after a
  // successful server toggle so the cached profile in localStorage reflects
  // the new status BEFORE the (possibly debounced or slow) server refresh.
  // This guarantees that killing + reopening the app restores the correct
  // online/offline state instead of falling back to stale cache.
  const setDriverAvailabilityLocal = useCallback((isAvailable: boolean) => {
    lastOptimisticWriteRef.current = Date.now();
    setDriverState(prev => {
      if (!prev) return prev;
      const next = { ...prev, is_available: isAvailable };
      saveDriverToStorage(next);
      return next;
    });
  }, []);

  const setDriverVehicleLocal = useCallback(
    (v: { vehicle_row_id: string; vehicle_id: string | null; vehicle_registration: string | null }) => {
      setDriverState(prev => {
        if (!prev) return prev;
        const next = {
          ...prev,
          vehicle_row_id: v.vehicle_row_id,
          vehicle_id: v.vehicle_id,
          vehicle_registration: v.vehicle_registration,
        };
        saveDriverToStorage(next);
        return next;
      });
    },
    [],
  );

  const sendOtp = async (phone: string) => {
    const { data, error } = await supabase.functions.invoke('driver-login', {
      body: { phone, action: 'send_otp' },
    });

    if (error || !data?.success) {
      return { success: false, error: data?.error || 'Failed to send OTP' };
    }

    return { success: true, driverName: data.driver_name };
  };

  const verifyOtp = async (phone: string, otp: string) => {
    // Mint a brand-new FCM token BEFORE invoking driver-login so the server
    // can create the push_tokens + driver_devices row atomically with the
    // new session id. This makes Verify OTP the single authoritative point
    // of token creation — no zero-token window, no post-login race.
    let mintedFcm: { token: string; platform: string } | null = null;
    try {
      // Fast path: use the FCM token primed at app boot. Avoids the
      // first-install race where Firebase Installations isn't initialised
      // yet and refreshToken() returns null.
      const primed = await waitForFcmToken(5000);
      if (primed) {
        mintedFcm = { token: primed, platform: 'android' };
      } else {
        mintedFcm = await mintFreshFcmToken();
      }
    } catch {
      mintedFcm = null;
    }

    // Collect the installed app version + build so the server can record it
    // on driver_devices for the "who is still on an old build" report. Native
    // only — web has no build number. Format: "1.0.4 (14)".
    let appVersion: string | null = null;
    try {
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const { App } = await import('@capacitor/app');
        const info = await App.getInfo();
        if (info?.version) {
          appVersion = info.build ? `${info.version} (${info.build})` : info.version;
        }
      }
    } catch {
      appVersion = null;
    }

    const { data, error } = await supabase.functions.invoke('driver-login', {
      body: {
        phone,
        action: 'verify_otp',
        otp_code: otp,
        fcm_token: mintedFcm?.token ?? null,
        platform: mintedFcm?.platform ?? null,
        app_version: appVersion,
      },
    });

    if (error || !data?.success) {
      return { success: false, error: data?.error || 'Verification failed' };
    }

    // Establish the session WITHOUT calling supabase.auth.setSession().
    //
    // setSession() does an inline `/auth/v1/user` validation round-trip
    // before persisting the tokens. On a flaky mobile network that round-trip
    // intermittently fails right after the long `driver-login` invoke and
    // the library throws away the perfectly valid tokens we just received —
    // surfacing as "Failed to establish session".
    //
    // Instead we write the session directly to the same localStorage key
    // (`driver-auth-token`) the supabase-js client reads from. The next call
    // to `supabase.auth.getSession()` (which is what `_getAccessToken` and
    // `functions.invoke` rely on) will pick up our session straight from
    // storage. Background validation runs after the UI is unblocked.
    try {
      const accessToken: string = data.access_token;
      const refreshToken: string = data.refresh_token;
      const [, payloadB64] = accessToken.split('.');
      const payload = JSON.parse(
        atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'))
      );
      const nowSec = Math.floor(Date.now() / 1000);
      const expiresAt: number = typeof payload.exp === 'number' ? payload.exp : nowSec + 3600;
      const session = {
        access_token: accessToken,
        refresh_token: refreshToken,
        token_type: 'bearer',
        expires_at: expiresAt,
        expires_in: Math.max(0, expiresAt - nowSec),
        provider_token: null,
        provider_refresh_token: null,
        user: {
          id: payload.sub,
          aud: payload.aud ?? 'authenticated',
          role: payload.role ?? 'authenticated',
          email: payload.email ?? null,
          phone: payload.phone ?? '',
          app_metadata: payload.app_metadata ?? { provider: 'email', providers: ['email'] },
          user_metadata: payload.user_metadata ?? {},
          identities: [],
          created_at: new Date(nowSec * 1000).toISOString(),
          updated_at: new Date(nowSec * 1000).toISOString(),
        },
      };
      try {
        localStorage.setItem('driver-auth-token', JSON.stringify(session));
      } catch (storageErr) {
        console.error('[auth] failed to persist session to storage', storageErr);
        return { success: false, error: 'Failed to establish session' };
      }
      // Make sure realtime channels created from now on use the new token
      // immediately, without waiting for an auth-state event.
      try { (supabase as any).realtime?.setAuth?.(accessToken); } catch { /* ignore */ }
      // Best-effort: tell the auth client about the new session in the
      // background so it emits SIGNED_IN, schedules auto-refresh, and
      // updates its in-memory state. We do NOT await this — UI must not
      // wait on the inline /auth/v1/user round-trip.
      setTimeout(() => {
        supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        }).catch(() => { /* storage already has it; ignore */ });
      }, 0);
    } catch (err) {
      console.error('[auth] verifyOtp session install failed', err);
      return { success: false, error: 'Failed to establish session' };
    }

    if (data.session_id) {
      setDriverSessionId(data.session_id);
      setSessionIdState(data.session_id);
    }
    forcedOutRef.current = false;
    intentionalSignOutRef.current = false;
    cancelRecovery();
    // Clear the force-signed-out marker so checkExistingSession on the
    // next reload trusts the freshly-minted session.
    clearForceSignedOutMarker();
    setDriver(data.driver);

    // Token creation is owned by driver-login.verify_otp (the FCM token
    // was passed in the request body above). Record that pair so the
    // useFcmRegistration safety net knows the server already has this
    // token and won't re-post it from the OS `registration` callback.
    if (mintedFcm?.token && data.driver?.id) {
      setLastRegisteredFcm({ driverId: data.driver.id, token: mintedFcm.token });
    }

    // Post-login self-heal: if the inline mint returned null (slow Play
    // Services, transient FCM failure) the server marked `fcm_pending`
    // and currently has zero token rows for this driver. Retry in the
    // background until a token row exists. Bounded: 6 attempts, ~30s.
    if ((!mintedFcm?.token || data.fcm_pending) && data.driver?.id) {
      const driverId: string = data.driver.id;
      (async () => {
        for (let attempt = 0; attempt < 6; attempt++) {
          const last = getLastRegisteredFcm();
          if (last && last.driverId === driverId) return;
          const primed = await waitForFcmToken(3000);
          let token: string | null = primed;
          if (!token) {
            const minted = await mintFreshFcmToken();
            token = minted?.token ?? null;
          }
          if (token) {
            await postRegisterFcmToken(driverId, token);
            const after = getLastRegisteredFcm();
            if (after && after.driverId === driverId) return;
          }
          await new Promise((r) => setTimeout(r, 5000));
        }
        console.warn('[auth] post-login FCM self-heal gave up after 6 attempts');
      })();
    }

    return { success: true };
  };

  const signOut = async () => {
    // Block sign-out while a trip is truly active. The local activeTripStore
    // can be stale (trip ended while app was killed/offline/backgrounded), so
    // validate against the DB before blocking. Only `in_progress` blocks.
    const active = activeTripStore.get();
    if (active?.bookingId) {
      let stillActive = true; // safe default on network failure
      try {
        const { data, error } = await supabase
          .from('bookings' as any)
          .select('status')
          .eq('id', active.bookingId)
          .maybeSingle();
        if (!error) {
          const status = (data as any)?.status as string | undefined;
          stillActive = status === 'in_progress';
        }
      } catch {
        stillActive = true;
      }
      if (stillActive) {
        toast.error("You can't log out during an active trip. Please complete or cancel the trip first.");
        return;
      }
      // Self-heal stale local entry so the rest of signOut + the Profile
      // Sign Out button (which reads from the same store) re-enable.
      activeTripStore.clear();
    }

    // Snapshot identifiers BEFORE clearing local state so the background
    // server-side cleanup below can still reach them.
    const driverIdSnapshot = driver?.id;
    const sessionIdSnapshot = getDriverSessionId();
    const cachedToken = getCachedFcmToken();

    // ── PHASE 1 (fully synchronous): clear ALL local auth state BEFORE
    // anything async runs. This is what guarantees that the caller can
    // navigate to /driver/login on the next line without DriverLogin's
    // `isAuthenticated && !loading -> navigate('/driver/home')` redirect
    // bouncing the user back to home (which would briefly flash the
    // Suspense spinner on a white background).
    setForceSignedOutMarker();
    intentionalSignOutRef.current = true;
    cancelRecovery();
    try { localStorage.removeItem(DRIVER_AUTH_TOKEN_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(DRIVER_STORAGE_KEY); } catch { /* ignore */ }
    setDriver(null);
    setDriverSessionId(null);
    setSessionIdState(null);
    clearPrimedFcmToken();

    // ── PHASE 2 (background, fire-and-forget): authoritative server-side
    // teardown. We do NOT await any of this — the user is already on the
    // login page. Failures are logged but never block.
    (async () => {
      // Stop the native FGS heartbeat so it can't resurrect
      // `presence_status='online'` / `is_available=true` a moment after
      // the offline flip lands.
      try { await GpsBuffer.stopHeartbeat(); } catch { /* non-native */ }

      // Unpair this driver from any vehicle (mirrors Vendor Fleet Management
      // rules) so the next login shows the vehicle selection screen.
      try {
        if (driverIdSnapshot) {
          await supabase.functions.invoke('driver-vehicle', {
            body: { action: 'unpair_vehicle', driver_id: driverIdSnapshot },
          });
        }
      } catch (err) {
        console.error('[signOut] unpair_vehicle failed:', err);
      }

      try { await supabase.auth.signOut(); } catch { /* ignore */ }

      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
          const { PushNotifications } = await import('@capacitor/push-notifications');
          await PushNotifications.removeAllListeners();
        }
      } catch { /* not native or plugin missing */ }

      // Force Offline + clear active_session_id atomically. The
      // `is_logout: true` flag tells dispatch-trip to bypass the session
      // guard (logout is authoritative) and to null `active_session_id`
      // so any in-flight FGS heartbeat racing past stopHeartbeat() above
      // is rejected on the server.
      try {
        if (driverIdSnapshot) {
          await supabase.functions.invoke('dispatch-trip', {
            body: {
              action: 'update_driver_availability',
              driver_id: driverIdSnapshot,
              session_id: sessionIdSnapshot,
              is_available: false,
              is_logout: true,
            },
          });
        }
      } catch (err) {
        console.error('[signOut] force-offline failed:', err);
      }

      // SOFT-deactivate ONLY this device's push_tokens row, scoped by token.
      try {
        if (driverIdSnapshot && cachedToken) {
          await supabase
            .from('push_tokens' as any)
            .update({ is_active: false, updated_at: new Date().toISOString() })
            .eq('user_id', driverIdSnapshot)
            .eq('user_type', 'driver')
            .eq('token', cachedToken);
        }
      } catch (err) {
        console.error('[signOut] push_tokens deactivate failed:', err);
      }

      try { await wipeDeviceFcmIdentity(); } catch { /* ignore */ }
      setLastRegisteredFcm(null);
    })();
  };

  // Drive the vehicle-availability heartbeat. Runs as long as the
  // DriverAuthProvider is mounted; the hook itself decides when to fire
  // based on isOnline / on-trip state.
  useDriverHeartbeat(driver?.id, !!driver?.is_available || hasApproachBooking);

  return (
    <DriverAuthContext.Provider value={{
      driver,
      loading,
      isAuthenticated: !!driver,
      sessionId,
      sendOtp,
      verifyOtp,
      signOut,
      refreshDriver,
      setDriverAvailabilityLocal,
      setDriverVehicleLocal,
    }}>
      {children}
    </DriverAuthContext.Provider>
  );
}

export function useDriverAuth() {
  const context = useContext(DriverAuthContext);
  if (!context) {
    throw new Error('useDriverAuth must be used within DriverAuthProvider');
  }
  return context;
}
